import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  PlusIcon, TrashIcon, MinusIcon, WarningIcon,
  StorefrontIcon, ShoppingBagIcon, BedIcon, BeerBottleIcon,
} from "@phosphor-icons/react";
import { inr } from "./posHelpers";

const ORDER_TYPES = [
  { id: "dine_in", label: "Dine-in", Icon: StorefrontIcon },
  { id: "takeaway", label: "Takeaway", Icon: ShoppingBagIcon },
  { id: "room_service", label: "In-room", Icon: BedIcon },
  { id: "bar", label: "Bar", Icon: BeerBottleIcon },
];

function RoomPicker({ occupiedRooms, selectedRoomId, setSelectedRoomId, testIdBase, warningTestId }) {
  const picked = occupiedRooms.find((r) => r.room_id === selectedRoomId);
  return (
    <>
      <Select value={selectedRoomId} onValueChange={setSelectedRoomId}>
        <SelectTrigger className="h-8 text-xs mt-1" data-testid={testIdBase === "charge" ? "pos-room-charge-select" : `pos-${testIdBase}-select`}><SelectValue placeholder="Pick room…" /></SelectTrigger>
        <SelectContent>
          {occupiedRooms.length === 0 && (
            <SelectItem value="__none" disabled>No rooms in this property</SelectItem>
          )}
          {occupiedRooms.map((r) => (
            <SelectItem key={r.room_id} value={r.room_id} data-testid={`pos-${testIdBase}-option-${r.room_number}`}>
              <span className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${r.is_checked_in ? "bg-secondary" : "bg-muted-foreground/40"}`} />
                <span className="font-mono-plex">#{r.room_number}</span>
                <span className="text-muted-foreground text-[10px]">·</span>
                <span>{r.is_checked_in ? r.guest_name : "vacant"}</span>
                {!r.is_checked_in && <span className="text-[9px] text-destructive uppercase tracking-wide">not checked-in</span>}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {picked && !picked.is_checked_in && (
        <div className="mt-1 rounded bg-destructive/10 border border-destructive/40 p-2 text-[10px] text-destructive flex items-start gap-1" data-testid={warningTestId}>
          <WarningIcon size={12} weight="duotone" className="mt-0.5 shrink-0" />
          <span>Room #{picked.room_number} is <strong>not currently checked-in</strong>. Switch payment to cash / UPI / card or check the guest in first.</span>
        </div>
      )}
      {picked && picked.is_checked_in && testIdBase === "room" && (
        <p className="text-[10px] text-muted-foreground mt-1" data-testid="pos-room-ok">
          Delivering to <strong>{picked.guest_name}</strong> · check-out {picked.check_out}. Charge posts to folio.
        </p>
      )}
    </>
  );
}

export function PosCart({
  cart, updateQty, removeLine,
  orderType, setOrderType,
  paymentMode, setPaymentMode,
  discount, setDiscount,
  discountType, setDiscountType,
  tableNo, setTableNo,
  customer, setCustomer,
  occupiedRooms, selectedRoomId, setSelectedRoomId,
  openTables, selectedTableOrderId, setSelectedTableOrderId,
  totals, submitOrder,
}) {
  return (
    <Card className="p-4 lg:sticky lg:top-4 h-fit">
      <p className="label-overline">Current order</p>

      <div className="mt-2 grid grid-cols-4 gap-1 rounded-md border border-border p-1" data-testid="pos-order-type-group">
        {ORDER_TYPES.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setOrderType(id)}
            className={`text-[10px] py-2 rounded flex flex-col items-center gap-1 transition-colors ${orderType === id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            data-testid={`pos-order-type-${id}`}
          >
            <Icon size={16} weight="duotone" />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {orderType === "room_service" && (
        <div className="mt-2 rounded-md border border-primary/40 bg-primary/5 p-2">
          <Label className="text-[10px] flex items-center gap-1"><BedIcon size={10} /> Deliver to room</Label>
          <RoomPicker
            occupiedRooms={occupiedRooms}
            selectedRoomId={selectedRoomId}
            setSelectedRoomId={setSelectedRoomId}
            testIdBase="room"
            warningTestId="pos-room-warning"
          />
        </div>
      )}

      {orderType === "dine_in" && openTables && openTables.length > 0 && (
        <div className="mt-2 rounded-md border border-secondary/40 bg-secondary/5 p-2">
          <Label className="text-[10px]">Post to open table (optional)</Label>
          <Select value={selectedTableOrderId || "__standalone"} onValueChange={(v) => setSelectedTableOrderId(v === "__standalone" ? "" : v)}>
            <SelectTrigger className="h-8 text-xs mt-1" data-testid="pos-open-table-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__standalone">Standalone order (no table)</SelectItem>
              {openTables.map((t) => (
                <SelectItem key={t.id} value={t.id}>Table {t.table_number} · {t.section} · {t.rounds_count || 0} round{t.rounds_count === 1 ? "" : "s"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground mt-1">Selecting a table adds this order as a new round on that bill instead of billing right now.</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mt-2">
        <Input placeholder={orderType === "dine_in" ? "Table #" : orderType === "bar" ? "Seat #" : "Ref"} value={tableNo} onChange={(e) => setTableNo(e.target.value)} className="h-8" data-testid="pos-table-no" />
        <Input placeholder="Customer" value={customer} onChange={(e) => setCustomer(e.target.value)} className="h-8" />
      </div>

      <div className="mt-3 space-y-2 max-h-72 overflow-y-auto pr-1">
        {cart.map((c) => (
          <div key={c.menu_item.id} className="flex items-center gap-2 text-sm border-b border-border/50 pb-2" data-testid={`pos-cart-item-${c.menu_item.id}`}>
            <div className="flex-1 truncate">
              <p className="truncate">
                {c.menu_item.name}
                {c.menu_item.unit_type === "peg" && <span className="text-[10px] text-muted-foreground ml-1">· {c.menu_item.peg_size_ml}ml peg</span>}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {inr(c.menu_item.price)}{c.menu_item.unit_type === "peg" ? "/peg" : ""} · {c.menu_item.gst_rate}%
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => updateQty(c.menu_item.id, -1)}><MinusIcon size={10} /></Button>
              <span className="text-sm font-mono-plex w-5 text-center">{c.qty}</span>
              <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => updateQty(c.menu_item.id, 1)}><PlusIcon size={10} /></Button>
            </div>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeLine(c.menu_item.id)}><TrashIcon size={10} /></Button>
          </div>
        ))}
        {cart.length === 0 && <p className="text-xs text-muted-foreground italic text-center py-6">Tap menu items to add.</p>}
      </div>

      <div className="mt-4 pt-3 border-t border-border space-y-1 text-sm">
        <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono-plex">{inr(totals.sub)}</span></div>
        <div className="flex justify-between text-muted-foreground"><span>GST</span><span className="font-mono-plex">{inr(totals.tax)}</span></div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">Discount</span>
          <Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="h-7 text-xs flex-1" />
          <div className="flex rounded border border-border overflow-hidden">
            <button type="button" onClick={() => setDiscountType?.("amount")} className={`px-2 text-[10px] ${(discountType || "amount") === "amount" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="pos-discount-amount">₹</button>
            <button type="button" onClick={() => setDiscountType?.("percent")} className={`px-2 text-[10px] ${discountType === "percent" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="pos-discount-percent">%</button>
          </div>
        </div>
        <div className="flex justify-between pt-2 border-t border-border/50 mt-2">
          <span className="font-medium">Total</span>
          <span className="font-display text-xl">{inr(totals.grand)}</span>
        </div>
      </div>

      <div className="mt-4">
        <Label className="text-xs">Payment</Label>
        <Select value={paymentMode} onValueChange={setPaymentMode}>
          <SelectTrigger className="mt-1" data-testid="pos-payment-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="upi">UPI</SelectItem>
            <SelectItem value="card">Card</SelectItem>
            <SelectItem value="room_charge">Post to room</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        {paymentMode === "room_charge" && orderType !== "room_service" && (
          <div className="mt-2 rounded-md border border-border p-2">
            <Label className="text-[10px]">Room</Label>
            <RoomPicker
              occupiedRooms={occupiedRooms}
              selectedRoomId={selectedRoomId}
              setSelectedRoomId={setSelectedRoomId}
              testIdBase="charge"
              warningTestId="pos-charge-warning"
            />
          </div>
        )}
      </div>

      <Button className="w-full mt-4" onClick={submitOrder} disabled={cart.length === 0} data-testid="pos-submit-order">
        Ring up · {inr(totals.grand)}
      </Button>
    </Card>
  );
}
